// ---------------------------------------------------------------------------
// Channels Service - V11 Discovery API
// ---------------------------------------------------------------------------
// API client for managing client's own social media channels
// ---------------------------------------------------------------------------

import { api } from '@/lib/api';

/**
 * B1 — period-scoped metrics and deltas. Present only when the request
 * was made with both `date_from` and `date_to`. Null otherwise.
 *
 * Delta semantics:
 *   number         → (current - previous) / previous, signed decimal
 *                    (0.18 = +18%, -0.291 = -29.1%)
 *                    OR, for engagementDelta, absolute percentage points
 *   null           → previous = 0 and current > 0 ("new" — UI renders pill)
 *   0              → both periods zero (no change)
 *
 * Values can be outside the ±1.0 range when the previous window is
 * sparse (see F2.2 DeltaIndicator for the capped-display rendering).
 */
export interface ChannelPeriod {
  readonly from: string;
  readonly to: string;
  readonly postsInPeriod: number;
  readonly likesInPeriod: number;
  readonly sharesInPeriod: number;
  readonly commentsInPeriod: number;
  readonly profilesAddedInPeriod: number;
  readonly engagementRateInPeriod: number;
  readonly postsDelta: number | null;
  readonly likesDelta: number | null;
  readonly sharesDelta: number | null;
  readonly commentsDelta: number | null;
  readonly profilesDelta: number | null;
  readonly engagementDelta: number | null;
}

export interface Channel {
  readonly channelId: number;
  readonly datasourceId: number;
  readonly datasourceItemId: number;
  readonly datasourceName: string;
  readonly authorId: number;
  readonly platformId: number;
  readonly username: string;
  readonly fullName: string | null;
  readonly profilePicUrl: string | null;
  readonly platform: string;
  readonly category: string;
  readonly categoryId: number;
  readonly scrapingEnabled: boolean;
  readonly followers: number;
  readonly totalPosts: number;
  readonly totalLikes: number;
  readonly totalShares: number;
  readonly totalViews: number;
  readonly totalPostComments: number;
  readonly lastActivity: string | null;
  readonly commentsReceived: number;
  readonly sentiment: number | null;
  readonly uniqueCommenters: number;
  readonly profilesAdded: number;
  readonly profilesProcessed: number;
  readonly brandsDetected: number;
  readonly teamsDetected: number;
  /**
   * @deprecated Not currently rendered in the Channels UI (see audit
   *   smell #8). Kept on the interface for API compatibility and for
   *   future consumers such as the Discovery table column. Channel
   *   freshness is now driven by `lastAnalysedAt` instead.
   */
  readonly analysisStatus: number | null;
  readonly analysisStart: string | null;
  readonly analysisEnd: string | null;
  /** B3 — freshness: MAX(datasource_dates.updated_at) where status=3 */
  readonly lastAnalysedAt: string | null;
  readonly createdAt: string;
  /** B1 — period-scoped metrics, present only when date_from+date_to supplied */
  readonly period: ChannelPeriod | null;
}

export interface ChannelsResponse {
  readonly success: boolean;
  readonly data: readonly Channel[];
  readonly meta: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
    readonly lastPage: number;
    readonly hasMorePages: boolean;
  };
}

export interface ChannelsFilters {
  readonly page?: number;
  readonly limit?: number;
  readonly sort?: string;
  readonly order?: 'asc' | 'desc';
  readonly search?: string;
  readonly category?: readonly number[];
  readonly platform?: readonly string[];
  readonly scraping_enabled?: boolean;
  readonly analysis_status?: readonly number[];
  readonly date_from?: string;
  readonly date_to?: string;
  readonly granularity?: 'daily' | 'weekly' | 'monthly';
  /** Include own channels in metrics (default: false) */
  readonly include_own_channels?: string;
}

export interface ChannelsTrendsResponse {
  readonly success: boolean;
  readonly data: {
    readonly metric: string;
    readonly groupBy: string;
    readonly granularity: string;
    readonly timeline: readonly Record<string, unknown>[];
    readonly periodTimeline?: readonly Record<string, unknown>[];
  };
}

export interface ChannelPeriodMetricsResponse {
  readonly success: boolean;
  readonly data: Readonly<Record<string, ChannelPeriod>>;
}

export const channelsService = {
  /**
   * Get all channels for current account
   */
  async getChannels(filters: ChannelsFilters = {}): Promise<ChannelsResponse> {
    const response = await api.get<ChannelsResponse>('/api/v11/discovery/channels', {
      params: filters,
    });
    return response;
  },

  /**
   * Get period metrics for specific channel IDs (parallel call).
   */
  async getPeriodMetrics(params: {
    date_from: string;
    date_to: string;
    include_own_channels?: string;
  }): Promise<ChannelPeriodMetricsResponse> {
    const response = await api.get<ChannelPeriodMetricsResponse>(
      '/api/v11/discovery/channels/period-metrics',
      { params }
    );
    return response;
  },

  /**
   * Get temporal trends for channels (posts, likes, comments, interactions over time)
   */
  async getTrends(metric: string, filters: ChannelsFilters = {}): Promise<ChannelsTrendsResponse> {
    const response = await api.get<ChannelsTrendsResponse>('/api/v11/discovery/channels/trends', {
      params: { metric, ...filters },
    });
    return response;
  },

  /**
   * Toggle scraping ON/OFF for a channel by channelId
   */
  async toggleScraping(
    datasourceItemId: number
  ): Promise<{ success: boolean; data: { id: number; scrapingEnabled: boolean } }> {
    const response = await api.patch<{
      success: boolean;
      data: { id: number; scrapingEnabled: boolean };
    }>(`/api/v11/discovery/channels/${datasourceItemId}/toggle`);
    return response;
  },
};
